import { useEffect, useState } from "react";
import api from "../../api";
import { ShieldAlert, RefreshCw, RotateCcw, Ban, CheckCircle2, History } from "lucide-react";

/**
 * Admin console for the security-hold queue (cross-user face/voice
 * mismatch + ambient-voice hard-stops). Renders pending holds up top
 * with resolve actions; history at the bottom. Auto-polls every 15 s
 * so a new hold surfaces without a manual refresh.
 */
export default function SecurityHolds() {
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, total: 0 });
  const [forfeited, setForfeited] = useState([]);
  const [busy, setBusy] = useState(false);
  const [restoreBusyId, setRestoreBusyId] = useState("");
  const [restoreToast, setRestoreToast] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const [pRes, hRes, fRes] = await Promise.all([
        api.get("/admin/security-holds"),
        api.get("/admin/security-holds?status_filter=all"),
        api.get("/admin/roleplay/forfeited-attempts"),
      ]);
      setPending(pRes.data.holds || []);
      setHistory((hRes.data.holds || []).filter((h) => h.status !== "pending"));
      setCounts(pRes.data.counts || { pending: 0, total: 0 });
      setForfeited(fRes.data.forfeited || []);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load holds.");
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const resolve = async (hold_id, action) => {
    const note = window.prompt(
      action === "approve_resume"
        ? "Note (optional) — why are you letting the candidate resume?"
        : action === "approve_reset"
        ? "Note (optional) — the candidate will have to restart this section from scratch."
        : "Note (required) — reject means this attempt is treated as a policy breach.",
      "",
    );
    if (action === "reject" && !note) return;
    setBusy(true);
    try {
      await api.post(`/admin/security-holds/${hold_id}/resolve`, { action, note });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to resolve.");
    } finally {
      setBusy(false);
    }
  };

  const restoreAttempt = async (session) => {
    const ok = window.confirm(
      `Restore ${session.user_name || session.user_email}'s forfeited Role Play attempt?\n\n` +
        `• Attempt #${session.attempt_no} will be wiped so they can start a fresh one from their hub.\n` +
        `• MCQ, Sales Pitch, voice enrolment and any certificate stay untouched.\n` +
        `• This action is logged in admin_actions for audit.\n\n` +
        `Use this ONLY when the attempt was forfeited due to a technical issue (lost network, device swap, mic-check failure, etc.).`,
    );
    if (!ok) return;
    setRestoreBusyId(session.id);
    setRestoreToast("");
    try {
      await api.post(`/admin/roleplay/restore-attempt/${session.id}`);
      setRestoreToast(
        `Restored — ${session.user_name || session.user_email} can retake their Role Play now.`,
      );
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to restore attempt.");
    } finally {
      setRestoreBusyId("");
    }
  };

  const reasonLabel = {
    face_mismatch: "Face mismatch",
    voice_mismatch: "Voice mismatch",
    ambient_voice: "Second voice detected",
    face_spoof: "Face spoof suspected",
    face_mismatch_at_voice_enrol: "Face vs ID mismatch at mic check",
  };

  // iter51/iter52 — base64 image keys on security_hold.evidence that should
  // render as side-by-side selfies in the admin UI instead of a wall of JSON.
  // We match ANY key ending in `selfie_base64` or `image_base64` (with or
  // without an underscore prefix / separator) so legacy holds that stored
  // the selfie under e.g. `selfiebase64` / `livselfie_base64` still render.
  const isSelfieKey = (k) => /(^|[_-])(selfie|image|photo|frame)[_-]?base64$/i.test(String(k || ""));
  const asImgSrc = (b64) => {
    if (!b64 || typeof b64 !== "string") return null;
    const trimmed = b64.trim();
    if (!trimmed) return null;
    return trimmed.startsWith("data:") ? trimmed : `data:image/jpeg;base64,${trimmed}`;
  };
  const labelFor = (k) => String(k || "")
    .replace(/_base64$/i, "")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const renderEvidence = (evidence) => {
    const entries = Object.entries(evidence || {});
    // Keep images even if the string is shorter than 50 chars so edge-cases
    // (admin-seeded test tiles, legacy clips) still show up — but reject
    // obviously-empty values so we never render a broken <img>.
    const images = entries.filter(([k, v]) => isSelfieKey(k) && typeof v === "string" && v.trim().length > 20);
    const scalars = Object.fromEntries(entries.filter(([k]) => !isSelfieKey(k)));
    const hasSelfieKeyButNoImage = entries.some(([k, v]) => isSelfieKey(k) && (!v || (typeof v === "string" && v.trim().length <= 20)));
    return (
      <div className="space-y-2" data-testid="hold-evidence-body">
        {images.length > 0 && (
          <div className="flex flex-wrap gap-3" data-testid="hold-evidence-images">
            {images.map(([k, v]) => (
              <figure key={k} className="flex-shrink-0" data-testid={`hold-evidence-img-${k}`}>
                <img
                  src={asImgSrc(v)}
                  alt={labelFor(k)}
                  className="w-40 h-40 object-cover border-2 border-[#E5E7EB] bg-black"
                  onError={(e) => {
                    // Replace broken <img> with a visible failed-load tile
                    // so admins see the problem instead of a silent black
                    // square they can't tell apart from the camera-off case.
                    const el = e.currentTarget;
                    el.style.display = "none";
                    const fallback = el.nextElementSibling;
                    if (fallback) fallback.style.display = "flex";
                  }}
                />
                <div
                  className="w-40 h-40 border-2 border-[#DC2626] bg-[#FEF2F2] text-[11px] text-[#991B1B] p-2 items-center justify-center text-center leading-tight font-mono"
                  style={{ display: "none" }}
                  data-testid={`hold-evidence-img-${k}-broken`}
                >
                  Image data unreadable
                  <br />
                  ({(v || "").length} chars)
                </div>
                <figcaption className="text-[10px] uppercase tracking-widest text-[#525252] mt-1 font-bold">
                  {labelFor(k)}
                </figcaption>
              </figure>
            ))}
          </div>
        )}
        {hasSelfieKeyButNoImage && images.length === 0 && (
          <div className="text-[11px] text-[#B45309] bg-[#FEF3C7] border-l-4 border-[#D97706] px-2 py-1 font-semibold">
            Selfie evidence key present but value is empty — the capture may have failed server-side.
          </div>
        )}
        {Object.keys(scalars).length > 0 && (
          <pre className="text-[11px] text-[#525252] whitespace-pre-wrap font-mono bg-white p-2 border border-[#E5E7EB] overflow-x-auto max-w-full">
            {JSON.stringify(scalars, null, 2)}
          </pre>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <ShieldAlert size={24} className="text-[#EE2737]" />
        <div>
          <div className="overline">SECURITY HOLDS · CROSS-USER + AMBIENT VOICE HARD-STOPS</div>
          <h1 className="text-2xl font-heading font-black">
            Pending holds <span className="text-[#EE2737]">{counts.pending}</span>
          </h1>
        </div>
        <button onClick={load} className="ml-auto btn btn-secondary text-xs" data-testid="security-holds-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {err && (
        <div className="bg-[#FEE2E2] border-l-4 border-[#EE2737] p-3 text-sm text-[#991B1B]">{err}</div>
      )}

      {restoreToast && (
        <div
          className="bg-[#D1FAE5] border-l-4 border-[#059669] p-3 text-sm text-[#065F46] flex items-center justify-between"
          data-testid="forfeited-restore-toast"
        >
          <span>{restoreToast}</span>
          <button
            onClick={() => setRestoreToast("")}
            className="text-[#065F46] opacity-70 hover:opacity-100 text-lg leading-none"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      <section className="card p-6" data-testid="security-holds-pending">
        <div className="overline mb-3">PENDING · REQUIRE ADMIN ACTION</div>
        {pending.length === 0 ? (
          <div className="text-sm text-[#525252] italic">No pending holds. Everything's clean.</div>
        ) : (
          <div className="space-y-3">
            {pending.map((h) => (
              <div key={h.id} className="border-l-4 border-[#EE2737] bg-[#FEF2F2] p-4" data-testid={`hold-row-${h.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-[#1A1A24]">
                      {h.user_name || h.user_email || h.user_id}
                    </div>
                    <div className="text-xs text-[#525252] mt-0.5">
                      <b>{reasonLabel[h.reason] || h.reason}</b> · Section:{" "}
                      <span className="uppercase font-semibold">{h.section}</span> ·{" "}
                      Opened {new Date(h.created_at).toLocaleString()}
                    </div>
                    {h.evidence && Object.keys(h.evidence).length > 0 && (
                      <details className="mt-2" open={h.reason === "face_mismatch_at_voice_enrol"}>
                        <summary className="text-[11px] uppercase tracking-widest text-[#878787] cursor-pointer font-bold">
                          Evidence
                        </summary>
                        <div className="mt-1">
                          {renderEvidence(h.evidence)}
                        </div>
                      </details>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button
                      disabled={busy}
                      onClick={() => resolve(h.id, "approve_resume")}
                      className="btn btn-secondary text-xs !justify-start"
                      data-testid={`hold-resume-${h.id}`}
                    >
                      <CheckCircle2 size={12} /> Approve · Resume
                    </button>
                    <button
                      disabled={busy}
                      onClick={() => resolve(h.id, "approve_reset")}
                      className="btn btn-secondary text-xs !justify-start"
                      data-testid={`hold-reset-${h.id}`}
                    >
                      <RotateCcw size={12} /> Approve · Fresh attempt
                    </button>
                    <button
                      disabled={busy}
                      onClick={() => resolve(h.id, "reject")}
                      className="btn text-xs !justify-start bg-[#EE2737] text-white hover:opacity-90"
                      data-testid={`hold-reject-${h.id}`}
                    >
                      <Ban size={12} /> Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="card p-6" data-testid="forfeited-roleplay-attempts">
        <div className="flex items-center justify-between mb-3">
          <div className="overline">FORFEITED ROLE PLAYS · RESTORE AFTER A TECHNICAL ISSUE</div>
          <span className="text-[11px] font-mono text-[#878787]">{forfeited.length}</span>
        </div>
        {forfeited.length === 0 ? (
          <div className="text-sm text-[#525252] italic">
            No forfeited role-play attempts. If a candidate loses an attempt to a
            genuine technical issue (lost network, device swap, mic-check error)
            it'll show up here with a one-click Restore.
          </div>
        ) : (
          <div className="space-y-3">
            {forfeited.map((s) => (
              <div
                key={s.id}
                className="border-l-4 border-[#D97706] bg-[#FFFBEB] p-4 flex items-start justify-between gap-4"
                data-testid={`forfeited-row-${s.id}`}
              >
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-[#1A1A24]">
                    {s.user_name || s.user_email || s.user_id}
                    {s.role_level && (
                      <span className="ml-2 text-[11px] font-mono bg-white border border-[#E5E7EB] px-1.5 py-0.5">
                        {s.role_level}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#525252] mt-0.5">
                    Attempt <b>#{s.attempt_no}</b> · Candidate spoke{" "}
                    <b>{s.turns_spoken}</b>{" "}
                    {s.turns_spoken === 1 ? "turn" : "turns"}
                    {s.abandoned_by_hold && (
                      <>
                        {" "}
                        · Forfeited by security hold{" "}
                        <span className="font-mono text-[11px] text-[#878787]">
                          {s.abandoned_by_hold.slice(0, 8)}
                        </span>
                      </>
                    )}
                    {" "}· Forfeited{" "}
                    {s.submitted_at && new Date(s.submitted_at).toLocaleString()}
                  </div>
                  {s.feedback && (
                    <div className="text-[11px] text-[#525252] mt-1 italic">
                      {s.feedback}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => restoreAttempt(s)}
                  disabled={restoreBusyId === s.id}
                  className="btn btn-secondary text-xs !justify-start flex-shrink-0 disabled:opacity-50"
                  data-testid={`forfeited-restore-${s.id}`}
                >
                  <History size={12} />
                  {restoreBusyId === s.id ? "Restoring…" : "Restore attempt"}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="card p-6" data-testid="security-holds-history">
        <div className="overline mb-3">HISTORY · RESOLVED ({history.length})</div>
        {history.length === 0 ? (
          <div className="text-sm text-[#525252] italic">No resolved holds yet.</div>
        ) : (
          <div className="divide-y divide-[#E5E7EB]">
            {history.slice(0, 30).map((h) => (
              <div key={h.id} className="py-3 flex items-start gap-3 text-sm">
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${h.status === "rejected" ? "bg-[#EE2737]" : "bg-[#059669]"}`} />
                <div className="min-w-0 flex-1">
                  <div className="truncate">
                    <b>{h.user_name || h.user_email}</b> · {reasonLabel[h.reason] || h.reason} · {h.section}
                  </div>
                  <div className="text-[11px] text-[#878787]">
                    {h.status.replace("_", " ")} by {h.resolved_by} ·{" "}
                    {h.resolved_at && new Date(h.resolved_at).toLocaleString()}
                    {h.admin_note && <span className="italic"> — "{h.admin_note}"</span>}
                  </div>
                  {/* iter52 — history section also renders the evidence so admins
                      can audit a resolved face-mismatch hold after the fact. */}
                  {h.evidence && Object.keys(h.evidence).length > 0 && (
                    <details className="mt-1">
                      <summary className="text-[10px] uppercase tracking-widest text-[#878787] cursor-pointer font-bold">
                        Evidence
                      </summary>
                      <div className="mt-1">
                        {renderEvidence(h.evidence)}
                      </div>
                    </details>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
